/**
 * Poker Coach Component
 * 
 * AI-powered poker coach that provides strategy advice,
 * hand analysis, and answers questions during gameplay.
 */

import React, { useState, useRef, useEffect } from 'react';
import { PokerCoachAI, ChatMessage, PokerAdvice, HandAnalysis } from '../services/PokerCoachAI';
import { VisibleGameState, ValidAction } from '../types';
import './PokerCoach.css';

export interface PokerCoachProps {
  gameState: VisibleGameState | null;
  playerId: string;
  validActions: ValidAction[];
  isPlayerTurn: boolean;
  className?: string;
}

export const PokerCoach: React.FC<PokerCoachProps> = ({
  gameState,
  playerId,
  validActions,
  isPlayerTurn,
  className = ''
}) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [advice, setAdvice] = useState<PokerAdvice | null>(null);
  const [handAnalysis, setHandAnalysis] = useState<HandAnalysis | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const coachRef = useRef<PokerCoachAI>(new PokerCoachAI());

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Welcome message
  useEffect(() => {
    if (messages.length === 0) {
      setMessages([{
        role: 'assistant',
        content: "👋 I'm your poker coach! I can help you with:\n• **Get Advice** — what to do right now\n• **Analyze Hand** — how strong your cards are\n• Ask me anything about poker strategy!",
        timestamp: Date.now()
      }]);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleGetAdvice = async () => {
    if (!gameState || isLoading) return;
    setIsLoading(true);

    const userMsg: ChatMessage = { role: 'user', content: '💡 What should I do here?', timestamp: Date.now() };
    setMessages(prev => [...prev, userMsg]);

    try {
      const result = await coachRef.current.getAdvice(gameState, playerId, validActions);
      setAdvice(result);

      const confidenceEmoji = result.confidence === 'high' ? '🟢' : result.confidence === 'medium' ? '🟡' : '🔴';
      const response: ChatMessage = {
        role: 'assistant',
        content: `${confidenceEmoji} **Recommendation: ${result.recommendation.toUpperCase()}**\n\n${result.reasoning}${result.handStrength ? `\n\n📊 Hand: ${result.handStrength}` : ''}${result.potOdds ? `\n💰 Pot Odds: ${result.potOdds}` : ''}`,
        timestamp: Date.now()
      };
      setMessages(prev => [...prev, response]);
    } catch (error) {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, I had trouble analyzing that. Try again.', timestamp: Date.now() }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAnalyzeHand = async () => {
    if (!gameState || isLoading) return;
    setIsLoading(true);

    const userMsg: ChatMessage = { role: 'user', content: '🃏 Analyze my hand', timestamp: Date.now() };
    setMessages(prev => [...prev, userMsg]);

    try {
      const result = await coachRef.current.analyzeHand(gameState, playerId);
      setHandAnalysis(result);

      const strengthEmoji = { premium: '🔥', strong: '💪', good: '👍', marginal: '🤔', weak: '👎' }[result.strength];
      const response: ChatMessage = {
        role: 'assistant',
        content: `${strengthEmoji} **${result.handName}** — ${result.strength.toUpperCase()}\n\n${result.explanation}${result.outs > 0 ? `\n\n🎯 Outs: ${result.outs}` : ''}${result.potOdds !== 'N/A' ? `\n💰 ${result.potOdds}` : ''}`,
        timestamp: Date.now()
      };
      setMessages(prev => [...prev, response]);
    } catch (error) {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, I had trouble analyzing that. Try again.', timestamp: Date.now() }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim() || !gameState || isLoading) return;

    const question = inputValue.trim();
    const userMsg: ChatMessage = { role: 'user', content: question, timestamp: Date.now() };
    setMessages(prev => [...prev, userMsg]);
    setInputValue('');
    setIsLoading(true);

    try {
      const answer = await coachRef.current.askQuestion(question, gameState, playerId);
      setMessages(prev => [...prev, { role: 'assistant', content: answer, timestamp: Date.now() }]);
    } catch (error) {
      setMessages(prev => [...prev, { role: 'assistant', content: "I'm having trouble connecting. Please try again.", timestamp: Date.now() }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleQuickQuestion = async (question: string) => {
    setInputValue('');
    const userMsg: ChatMessage = { role: 'user', content: question, timestamp: Date.now() };
    setMessages(prev => [...prev, userMsg]);
    setIsLoading(true);

    try {
      if (gameState) {
        const answer = await coachRef.current.askQuestion(question, gameState, playerId);
        setMessages(prev => [...prev, { role: 'assistant', content: answer, timestamp: Date.now() }]);
      }
    } catch (error) {
      setMessages(prev => [...prev, { role: 'assistant', content: "I'm having trouble connecting. Please try again.", timestamp: Date.now() }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleClearHistory = () => {
    coachRef.current.clearHistory();
    setMessages([{
      role: 'assistant',
      content: "👋 Chat cleared! I'm ready to help with your next hand.",
      timestamp: Date.now()
    }]);
    setAdvice(null);
    setHandAnalysis(null);
  };

  return (
    <div className={`poker-coach ${isExpanded ? 'poker-coach--expanded' : ''} ${className}`}>
      <button
        className="poker-coach__toggle"
        onClick={() => setIsExpanded(!isExpanded)}
        aria-label={isExpanded ? 'Collapse poker coach' : 'Expand poker coach'}
      >
        <span className="poker-coach__toggle-icon">🧠</span>
        <span className="poker-coach__toggle-text">
          {isExpanded ? 'Hide Coach' : 'Poker Coach'}
        </span>
        {!isExpanded && isPlayerTurn && (
          <span className="poker-coach__pulse" />
        )}
      </button>

      {isExpanded && (
        <div className="poker-coach__panel">
          {/* Action buttons */}
          <div className="poker-coach__actions">
            <button
              className="poker-coach__action-btn poker-coach__action-btn--advice"
              onClick={handleGetAdvice}
              disabled={isLoading || !gameState || !isPlayerTurn}
              title={!isPlayerTurn ? 'Wait for your turn' : 'Get strategic advice'}
            >
              💡 Get Advice
            </button>
            <button
              className="poker-coach__action-btn poker-coach__action-btn--analyze"
              onClick={handleAnalyzeHand}
              disabled={isLoading || !gameState}
            >
              🃏 Analyze Hand
            </button>
          </div>

          {/* Messages */}
          <div className="poker-coach__messages">
            {messages.map((msg, i) => (
              <div key={i} className={`poker-coach__message poker-coach__message--${msg.role}`}>
                <div className="poker-coach__message-content"
                  dangerouslySetInnerHTML={{ __html: formatMessage(msg.content) }}
                />
              </div>
            ))}
            {isLoading && (
              <div className="poker-coach__message poker-coach__message--assistant">
                <div className="poker-coach__typing">
                  <span /><span /><span />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Quick questions */}
          <div className="poker-coach__quick-questions">
            <button onClick={() => handleQuickQuestion('Should I bluff here?')} disabled={isLoading}>Bluff?</button>
            <button onClick={() => handleQuickQuestion('What are my outs?')} disabled={isLoading}>Outs?</button>
            <button onClick={() => handleQuickQuestion('What are pot odds?')} disabled={isLoading}>Pot Odds</button>
            <button onClick={() => handleQuickQuestion('Is my position good?')} disabled={isLoading}>Position</button>
          </div>

          {/* Input */}
          <div className="poker-coach__input-area">
            <input
              type="text"
              className="poker-coach__input"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask about strategy..."
              disabled={isLoading}
            />
            <button
              className="poker-coach__send-btn"
              onClick={handleSendMessage}
              disabled={isLoading || !inputValue.trim()}
            >
              ↑
            </button>
          </div>

          {/* Footer */}
          <div className="poker-coach__footer">
            <button className="poker-coach__clear-btn" onClick={handleClearHistory}>
              Clear Chat
            </button>
            <span className="poker-coach__disclaimer">AI advice — use your judgment</span>
          </div>
        </div>
      )}
    </div>
  );
};

// Simple markdown-like formatting for messages
function formatMessage(content: string): string {
  return content
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/\n/g, '<br/>');
}
